/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone, OnInit} from '@angular/core';
import {completeCopyModal} from '../../common/util/complete-copy-modal';
import {Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';

import {nextCopyTitle} from '../../../components/util/next-copy-title';
import {Uuid} from '../../../components/util/uuid';
import {DataExportResource} from '../../../components/api/resources/resource/data-export.resource';

export interface DataExportCopyResult {
  model: any;
  destination: boolean;
}

@Component({
  standalone: false,
  selector: 'adn-data-export-copy-modal',
  template: `
    <form class="form-horizontal" (ngSubmit)="copy()">
      <div class="modal-header dialogAddStuff">
        <button type="button" class="close" (click)="dismiss()">&times;</button>
        <h4 class="modal-title">
          <span>Data Export</span>
          <small>{{ originalName }}</small>
        </h4>
      </div>
      <div class="modal-body">
        <simple-error-messages [errors]="validationErrors"></simple-error-messages>
        <div class="form-group">
          <label class="col-md-3 control-label">{{ 'label.name' | ogTranslate }}</label>
          <div class="col-md-9">
            <input type="text" class="form-control" name="dataExport.name" [(ngModel)]="name" required>
          </div>
        </div>
        <div class="form-group">
          <label class="col-md-3 control-label">{{ 'label.description' | ogTranslate }}</label>
          <div class="col-md-9">
            <input type="text" class="form-control" name="dataExport.description" [(ngModel)]="description">
          </div>
        </div>
        <div class="checkbox text-right">
          <label class="checkbox">
            <input type="checkbox" [(ngModel)]="destination" name="dataExport.destination">
            <span class="small">Go to new object after copy</span>
          </label>
        </div>
      </div>
      <div class="modal-footer">
        <a class="btn btn-link btn-xs" (click)="dismiss()">{{ 'button.cancel' | ogTranslate }}</a>
        <button type="submit" class="btn btn-default" [disabled]="pending">{{ 'label.copy' | ogTranslate }}</button>
      </div>
    </form>
  `
})
export class DataExportCopyModalComponent implements OnInit {
  model: any;

  readonly onCopied = new Subject<DataExportCopyResult>();

  originalName = '';
  name = '';
  description = '';
  destination = true;
  pending = false;
  validationErrors: any[] = [];

  constructor(
    public bsModalRef: BsModalRef,
    @Inject(DataExportResource) private readonly DataExport: any,
    private readonly ngZone: NgZone
  ) {
  }

  ngOnInit(): void {
    this.originalName = this.model?.name || '';
    this.name = nextCopyTitle(this.model?.name || '');
    this.description = this.model?.description || '';
  }

  copy(): void {
    if (this.pending) {
      return;
    }

    this.pending = true;
    this.validationErrors = [];

    const copyModel = {
      id: this.model?.id,
      name: this.name,
      description: this.description
    };
    const newId = Uuid.generate();

    this.DataExport.copyDataExport(copyModel, newId).$promise.then((saved: any) => {
      this.ngZone.run(() => {
        const model = saved?.id ? saved : {id: newId};
        completeCopyModal(this.bsModalRef, this.onCopied, {model, destination: this.destination});
      });
    }, (response: any) => {
      this.ngZone.run(() => {
        this.validationErrors = response?.data?.errors || [];
        this.pending = false;
      });
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }
}
